import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../services/api';

const AdoptionContext = createContext(null);

// Map route paths to page identifiers used by the adoption backend
function routeToPageKey(pathname) {
  const segments = pathname.replace('/practice-view/', '').replace('/admin/', '').split('/');
  return segments[0] || 'dashboard';
}

export function AdoptionProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [pageContent, setPageContent] = useState({ walkthroughs: [], contextual: [], articles: [] });
  const [userProgress, setUserProgress] = useState(null);
  const [activeWalkthrough, setActiveWalkthrough] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  // Fetch content when page changes
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const page = routeToPageKey(location.pathname);
    api.getAdoptionPageContent(page)
      .then(res => {
        if (res.success) setPageContent(res.data);
      })
      .catch(() => {});
  }, [location.pathname, isAuthenticated, user]);

  // Fetch user progress on mount
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    api.getAdoptionProgress()
      .then(res => {
        if (res.success) setUserProgress(res.data);
      })
      .catch(() => {});
  }, [isAuthenticated, user]);

  const recordEvent = useCallback((eventType, entityType, entityId, metadata) => {
    api.postAdoptionEvent({ event_type: eventType, entity_type: entityType, entity_id: entityId, metadata })
      .catch(() => {});
  }, []);

  const startWalkthrough = useCallback((walkthrough) => {
    // Navigate to the walkthrough's target page if not already there
    const targetPage = walkthrough.trigger_config?.page;
    if (targetPage) {
      const pageKey = routeToPageKey(location.pathname);
      if (pageKey !== targetPage) {
        const path = targetPage === 'dashboard' ? '/practice-view' : `/practice-view/${targetPage}`;
        navigate(path);
      }
    }
    setActiveWalkthrough(walkthrough);
    setCurrentStep(0);
    recordEvent('walkthrough_started', 'walkthrough', walkthrough.id);
  }, [recordEvent, location.pathname, navigate]);

  const nextStep = useCallback(() => {
    if (!activeWalkthrough) return;
    const steps = activeWalkthrough.steps || [];
    if (currentStep < steps.length - 1) {
      const nextIdx = currentStep + 1;
      const nextStepData = steps[nextIdx];
      // Navigate to step's page if specified and different from current
      if (nextStepData?.page) {
        const pageKey = routeToPageKey(location.pathname);
        if (pageKey !== nextStepData.page) {
          const path = nextStepData.page === 'dashboard' ? '/practice-view' : `/practice-view/${nextStepData.page}`;
          navigate(path);
        }
      }
      setCurrentStep(s => s + 1);
    } else {
      // Walkthrough complete
      recordEvent('walkthrough_completed', 'walkthrough', activeWalkthrough.id);
      setActiveWalkthrough(null);
      setCurrentStep(0);
      // Refresh progress
      api.getAdoptionProgress().then(res => {
        if (res.success) setUserProgress(res.data);
      }).catch(() => {});
    }
  }, [activeWalkthrough, currentStep, recordEvent, location.pathname, navigate]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      const prevIdx = currentStep - 1;
      const prevStepData = (activeWalkthrough?.steps || [])[prevIdx];
      if (prevStepData?.page) {
        const pageKey = routeToPageKey(location.pathname);
        if (pageKey !== prevStepData.page) {
          const path = prevStepData.page === 'dashboard' ? '/practice-view' : `/practice-view/${prevStepData.page}`;
          navigate(path);
        }
      }
      setCurrentStep(s => s - 1);
    }
  }, [currentStep, activeWalkthrough, location.pathname, navigate]);

  const skipWalkthrough = useCallback(() => {
    if (activeWalkthrough) {
      recordEvent('walkthrough_skipped', 'walkthrough', activeWalkthrough.id);
    }
    setActiveWalkthrough(null);
    setCurrentStep(0);
  }, [activeWalkthrough, recordEvent]);

  const dismissContent = useCallback((contentId) => {
    recordEvent('tooltip_dismissed', 'contextual_content', contentId);
    setPageContent(prev => ({
      ...prev,
      contextual: prev.contextual.filter(c => c.id !== contentId),
    }));
  }, [recordEvent]);

  const toggleHelp = useCallback(() => {
    setHelpOpen(prev => {
      if (!prev) recordEvent('help_opened');
      return !prev;
    });
  }, [recordEvent]);

  const value = {
    pageContent,
    userProgress,
    activeWalkthrough,
    currentStep,
    helpOpen,
    startWalkthrough,
    nextStep,
    prevStep,
    skipWalkthrough,
    dismissContent,
    recordEvent,
    toggleHelp,
    setHelpOpen,
  };

  return (
    <AdoptionContext.Provider value={value}>
      {children}
    </AdoptionContext.Provider>
  );
}

export function useAdoption() {
  const context = useContext(AdoptionContext);
  if (!context) {
    throw new Error('useAdoption must be used within an AdoptionProvider');
  }
  return context;
}

export default AdoptionContext;
